
import { useContext, useState } from "react";
import { GlobalContext } from "../../Context/GlobalContext";
import { toast } from "react-toastify";

const API = {
  LIST: "/Inventory/v1.0/getVendors",
  ADD: "/Inventory/v1.0/addVendor",
  EDIT: "/Inventory/v1.0/editVendor",
  DELETE: "/Inventory/v1.0/deleteVendor",
  ITEM_DETAILS: "/Inventory/v1.0/getInventoryItemDetails",
  CREATE_PURCHASE: "/Inventory/v1.0/createPurchaseOrder",
};

export default function useVendors() {
  const { api, userID } = useContext(GlobalContext);

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(false);

const call = async (path, payload) => {
  const response = await api.post(path, payload);
  return response?.data;
};

  const fetchVendors = async () => {
    try {
      setLoading(true);
      const data = await call(API.LIST, { userid: userID });

    if (data?.responseCode === "00") {
  setVendors(data?.response || []);
} else {
  setVendors([]);
  toast.error(
    data?.responseMessage || "Failed to fetch vendors"
  );
}
    } catch (err) {
      console.error(err);
      toast.error("Network Error: Could not fetch vendors");
      setVendors([]);
    } finally {
      setLoading(false);
    }
  };

  const addVendor = async (vendorData) => {
    try {
      setLoading(true);
     const data = await call(API.ADD, {
  ...vendorData,
  userid: userID,
});

if (data?.responseCode === "00") {
  toast.success(
    data?.responseMessage || "Vendor added successfully"
  );

  await fetchVendors();
} else {
  toast.error(
    data?.responseMessage || "Failed to add vendor"
  );
}
    } catch (err) {
      toast.error("Error connecting to server");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const editVendor = async (vendorData) => {
    try {
      setLoading(true);
     const data = await call(API.EDIT, { ...vendorData, userid: userID });

      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Vendor updated successfully");
        fetchVendors();
      } else {
        toast.error(data?.responseMessage);
      }
    } catch (err) {
      toast.error("Error updating vendor");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const deleteVendor = async (vendorId) => {
    try {
      setLoading(true);
      const data = await call(API.DELETE, { userid: userID, vendorId });

      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Vendor deleted");
        fetchVendors();
      } else {
        toast.error(data?.responseMessage || "Could not delete vendor");
      }
    } catch (err) {
      toast.error("Error deleting vendor");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getItemDetails = async (itemId) => {
    try {
      const data = await call(API.ITEM_DETAILS, { userid: userID, itemId });

      if (data?.responseCode === "00") {
        // Usually, we don't toast for "getting" details unless it's an error
        return data?.response;
      } else {
        toast.warning(data?.responseMessage || "Item details not found");
        return null;
      }
    } catch (err) {
      console.error(err);
      return null;
    }
  };

  const createPurchase = async (vendorId, items) => {
    try {
      setLoading(true);
      const payload = {
        userid: String(userID),
        vendorId: String(vendorId),
        items: items.map((i) => ({
          itemId: String(i.item_id),
          quantity: String(i.quantity),
          purchasePrice: String(i.purchasePrice),
        })),
      };

      const data = await call(API.CREATE_PURCHASE, payload);

      if (data?.responseCode === "00") {
        toast.success(data?.responseMessage || "Purchase Order Created");
      } else {
        toast.error(data?.responseMessage || "Failed to create purchase");
      }
      return data;
    } catch (err) {
      toast.error("Critical error creating purchase order");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    vendors,
    loading,
    fetchVendors,
    addVendor,
    editVendor,
    deleteVendor,
    getItemDetails,
    createPurchase,
  };
}
